import React, { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Loader2, Send, Mail, MessageSquare, RefreshCw, AlertTriangle, CheckCircle2,
  Clock, XCircle, Eye, RotateCcw, ChevronDown, ChevronUp, FileText,
  Wifi, WifiOff, Bell, Filter, BarChart3, ArrowUpRight, Search
} from "lucide-react";

const CommunicationsPage = () => {
  const { token } = useAuth();
  const [tab, setTab] = useState("log");
  const [loading, setLoading] = useState(true);
  const [activeStatFilter, setActiveStatFilter] = useState(null);
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [templates, setTemplates] = useState({});
  const [failures, setFailures] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [exceptionsOpen, setExceptionsOpen] = useState(0);
  const [waStatus, setWaStatus] = useState(null);
  const [patients, setPatients] = useState([]);

  // Filters
  const [channelFilter, setChannelFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Send message form
  const [showSend, setShowSend] = useState(false);
  const [sendForm, setSendForm] = useState({ patient_id: "", template_id: "booking_confirmation", channel: "email", custom_message: "" });
  const [sending, setSending] = useState(false);

  // Template editing
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ subject: "", email_body: "", whatsapp_body: "", wa_template_name: "" });
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Exception resolution
  const [resolvingId, setResolvingId] = useState(null);
  const [resolveNotes, setResolveNotes] = useState("");

  // Expanded event
  const [expandedEvent, setExpandedEvent] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };
  const jsonHeaders = { ...headers, "Content-Type": "application/json" };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (channelFilter !== "all") params.set("channel", channelFilter);
      if (statusFilter !== "all") params.set("status", statusFilter);

      const [sRes, eRes, tRes, fRes, xRes, wRes, pRes] = await Promise.all([
        fetch(`${API}/staff/communication-stats`, { headers }),
        fetch(`${API}/staff/communication-events?${params}`, { headers }),
        fetch(`${API}/staff/message-templates`, { headers }),
        fetch(`${API}/staff/communication-failures?limit=30`, { headers }),
        fetch(`${API}/staff/exception-events?limit=50`, { headers }),
        fetch(`${API}/staff/whatsapp-status`, { headers }),
        fetch(`${API}/admin/users?limit=200`, { headers }),
      ]);
      if (sRes.ok) setStats(await sRes.json());
      if (eRes.ok) { const d = await eRes.json(); setEvents(d.events || []); setEventsTotal(d.total || 0); }
      if (tRes.ok) { const d = await tRes.json(); setTemplates(d.templates || {}); }
      if (fRes.ok) { const d = await fRes.json(); setFailures(d.failures || []); }
      if (xRes.ok) { const d = await xRes.json(); setExceptions(d.events || []); setExceptionsOpen(d.open_count || 0); }
      if (wRes.ok) setWaStatus(await wRes.json());
      if (pRes.ok) { const d = await pRes.json(); setPatients((d.users || d).filter(u => u.role === "patient")); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token, channelFilter, statusFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSend = async () => {
    if (!sendForm.patient_id) { toast.error("Select a patient"); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/staff/send-message`, {
        method: "POST", headers: jsonHeaders,
        body: JSON.stringify(sendForm),
      });
      if (res.ok) {
        const d = await res.json();
        const r = d.results?.[0];
        if (r?.status === "sent") toast.success(`Message sent via ${r.channel}`);
        else if (r?.status === "failed") toast.error(`Failed: ${r.error || "Unknown error"}`);
        else toast.info(`Message ${r?.status || "processed"}`);
        setShowSend(false);
        setSendForm({ patient_id: "", template_id: "booking_confirmation", channel: "email", custom_message: "" });
        fetchAll();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to send");
      }
    } catch { toast.error("Connection error"); }
    finally { setSending(false); }
  };

  const handleRetry = async (eventId) => {
    try {
      const res = await fetch(`${API}/staff/communication-failures/${eventId}/retry`, { method: "POST", headers });
      if (res.ok) { const d = await res.json(); toast.success(d.message); fetchAll(); }
      else { const e = await res.json(); toast.error(e.detail || "Retry failed"); }
    } catch { toast.error("Connection error"); }
  };

  const handleResolveComm = async (eventId) => {
    try {
      const res = await fetch(`${API}/staff/communication-failures/${eventId}/resolve?notes=Manually resolved`, { method: "POST", headers });
      if (res.ok) { toast.success("Marked as resolved"); fetchAll(); }
    } catch { toast.error("Failed"); }
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    setSavingTemplate(true);
    try {
      const res = await fetch(`${API}/staff/message-templates`, {
        method: "PUT", headers: jsonHeaders,
        body: JSON.stringify({ template_id: editingTemplate, ...templateForm }),
      });
      if (res.ok) { toast.success("Template updated"); setEditingTemplate(null); fetchAll(); }
      else toast.error("Failed to update template");
    } catch { toast.error("Connection error"); }
    finally { setSavingTemplate(false); }
  };

  const handleResetTemplate = async (templateId) => {
    try {
      const res = await fetch(`${API}/staff/message-templates/reset/${templateId}`, { method: "POST", headers });
      if (res.ok) { toast.success("Template reset to default"); fetchAll(); }
    } catch { toast.error("Failed"); }
  };

  const handleDetectFailures = async () => {
    try {
      const res = await fetch(`${API}/staff/failure-workflows/detect`, { method: "POST", headers });
      if (res.ok) { const d = await res.json(); toast.success(d.message); fetchAll(); }
    } catch { toast.error("Scan failed"); }
  };

  const handleResolveException = async (exceptionId) => {
    try {
      const res = await fetch(`${API}/staff/exception-events/${exceptionId}/resolve`, {
        method: "POST", headers: jsonHeaders,
        body: JSON.stringify({ resolution_notes: resolveNotes, action_taken: "Manual resolution" }),
      });
      if (res.ok) { toast.success("Exception resolved"); setResolvingId(null); setResolveNotes(""); fetchAll(); }
    } catch { toast.error("Failed"); }
  };

  const handleEscalate = async (exceptionId) => {
    try {
      const res = await fetch(`${API}/staff/exception-events/${exceptionId}/escalate`, { method: "POST", headers });
      if (res.ok) { const d = await res.json(); toast.success(d.message); fetchAll(); }
    } catch { toast.error("Failed"); }
  };

  const handleTriggerReminders = async () => {
    try {
      const res = await fetch(`${API}/staff/reminders/send-appointment-reminders`, { method: "POST", headers });
      if (res.ok) { const d = await res.json(); toast.success(d.message); fetchAll(); }
    } catch { toast.error("Failed"); }
  };

  const statusBadge = (s) => {
    const map = {
      queued: { cls: "bg-stone-100 text-stone-600 border-stone-200", icon: Clock },
      sent: { cls: "bg-blue-50 text-blue-700 border-blue-200", icon: Send },
      delivered: { cls: "bg-green-50 text-green-700 border-green-200", icon: CheckCircle2 },
      read: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: Eye },
      failed: { cls: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
      bounced: { cls: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
      manually_resolved: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: CheckCircle2 },
    };
    const cfg = map[s] || map.queued;
    const Icon = cfg.icon;
    return <Badge className={`${cfg.cls} text-xs`}><Icon className="w-3 h-3 mr-1" />{s?.replace("_", " ")}</Badge>;
  };

  const severityBadge = (s) => {
    const map = {
      low: "bg-stone-100 text-stone-600",
      medium: "bg-amber-50 text-amber-700",
      high: "bg-red-50 text-red-700",
      critical: "bg-red-100 text-red-800 font-bold",
    };
    return <Badge className={`${map[s] || map.low} text-xs`}>{s}</Badge>;
  };

  const filteredEvents = searchQuery
    ? events.filter(e => (e.patient_name || "").toLowerCase().includes(searchQuery.toLowerCase()) || (e.template_id || "").toLowerCase().includes(searchQuery.toLowerCase()))
    : events;

  if (loading) return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;

  return (
    <div className="space-y-6" data-testid="communications-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">Communications</h1>
          <p className="mt-1 text-base text-stone-500">Message templates, delivery tracking, and failure handling.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setShowSend(!showSend)} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg" data-testid="send-message-btn">
            <Send className="w-4 h-4 mr-1.5" /> Send Message
          </Button>
          <Button variant="outline" onClick={fetchAll} className="rounded-lg" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="comms-stats">
          <StatCard label="Total" value={stats.total} icon={BarChart3} />
          <StatCard label="Sent" value={stats.sent} icon={Send} color="text-blue-600" />
          <StatCard label="Delivered" value={stats.delivered} icon={CheckCircle2} color="text-green-600" />
          <StatCard label="Read" value={stats.read} icon={Eye} color="text-emerald-600" />
          <StatCard label="Failed" value={stats.failed} icon={XCircle} color="text-red-600" />
          <StatCard label="Email" value={stats.by_channel?.email || 0} icon={Mail} />
          <StatCard label="WhatsApp" value={stats.by_channel?.whatsapp || 0} icon={MessageSquare} color={waStatus?.configured ? "text-green-600" : "text-stone-400"} />
        </div>
      )}

      {/* WhatsApp Status */}
      {waStatus && (
        <Card className={`rounded-xl p-4 flex items-center gap-3 ${waStatus.configured ? "border-green-200 bg-green-50/30" : "border-amber-200 bg-amber-50/30"}`} data-testid="whatsapp-status">
          {waStatus.configured ? <Wifi className="w-5 h-5 text-green-600" /> : <WifiOff className="w-5 h-5 text-amber-600" />}
          <div className="flex-1">
            <p className="text-sm font-medium text-stone-900">
              WhatsApp Business Platform: {waStatus.configured ? "Connected" : "Not configured"}
            </p>
            <p className="text-xs text-stone-500">
              {waStatus.configured
                ? `Graph API ${waStatus.graph_version} - Phone ID and access token active`
                : "Set META_PHONE_NUMBER_ID and META_ACCESS_TOKEN in environment to enable WhatsApp messaging"}
            </p>
          </div>
        </Card>
      )}

      {/* Send Message Form */}
      {showSend && (
        <Card className="rounded-xl border-[#FF7A00]/30 p-5 space-y-3" data-testid="send-form">
          <h3 className="font-bold text-stone-900">Send message</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            <Select value={sendForm.patient_id} onValueChange={v => setSendForm({ ...sendForm, patient_id: v })}>
              <SelectTrigger className="rounded-lg" data-testid="send-patient-select"><SelectValue placeholder="Select patient" /></SelectTrigger>
              <SelectContent>{patients.map(p => <SelectItem key={p.user_id} value={p.user_id}>{p.name} ({p.email})</SelectItem>)}</SelectContent>
            </Select>
            <Select value={sendForm.template_id} onValueChange={v => setSendForm({ ...sendForm, template_id: v })}>
              <SelectTrigger className="rounded-lg" data-testid="send-template-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(templates).map(([k, v]) => <SelectItem key={k} value={k}>{v.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={sendForm.channel} onValueChange={v => setSendForm({ ...sendForm, channel: v })}>
              <SelectTrigger className="rounded-lg" data-testid="send-channel-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="both">Both</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Textarea
            placeholder="Custom message (optional - overrides template body)"
            value={sendForm.custom_message}
            onChange={e => setSendForm({ ...sendForm, custom_message: e.target.value })}
            className="rounded-lg"
            rows={3}
            data-testid="send-custom-message"
          />
          <div className="flex gap-2">
            <Button onClick={handleSend} disabled={sending} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg" data-testid="send-confirm-btn">
              {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send
            </Button>
            <Button variant="ghost" onClick={() => setShowSend(false)}>Cancel</Button>
          </div>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-stone-100 rounded-lg flex-wrap">
          <TabsTrigger value="log" className="rounded-md text-sm" data-testid="tab-log">Message Log ({eventsTotal})</TabsTrigger>
          <TabsTrigger value="templates" className="rounded-md text-sm" data-testid="tab-templates">Templates ({Object.keys(templates).length})</TabsTrigger>
          <TabsTrigger value="failures" className="rounded-md text-sm" data-testid="tab-failures">
            Failures ({failures.length})
          </TabsTrigger>
          <TabsTrigger value="exceptions" className="rounded-md text-sm" data-testid="tab-exceptions">
            Exceptions {exceptionsOpen > 0 && <Badge className="ml-1 bg-red-100 text-red-700 text-xs">{exceptionsOpen}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="automation" className="rounded-md text-sm" data-testid="tab-automation">Automation</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* TAB: Message Log */}
      {tab === "log" && (
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input placeholder="Search by patient or template..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 rounded-lg" data-testid="log-search" />
            </div>
            <Select value={channelFilter} onValueChange={v => { setChannelFilter(v); }}>
              <SelectTrigger className="w-36 rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All channels</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); }}>
              <SelectTrigger className="w-36 rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="read">Read</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filteredEvents.length > 0 ? (
            <div className="space-y-2">
              {filteredEvents.map(ev => (
                <Card key={ev.event_id} className="rounded-xl border-stone-200/60 p-4 hover:shadow-sm transition-shadow">
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${ev.channel === "whatsapp" ? "bg-green-100" : "bg-blue-100"}`}>
                      {ev.channel === "whatsapp" ? <MessageSquare className="w-4 h-4 text-green-600" /> : <Mail className="w-4 h-4 text-blue-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-stone-900">{ev.patient_name || ev.patient_id}</span>
                        {statusBadge(ev.status)}
                        <Badge variant="outline" className="text-xs">{(templates[ev.template_id]?.name || ev.template_id || "").replace(/_/g, " ")}</Badge>
                      </div>
                      <p className="text-xs text-stone-400 mt-0.5">
                        {ev.channel} - {ev.sent_by_name || "System"} - {ev.created_at?.split("T")[0]} {ev.created_at?.split("T")[1]?.split(".")[0]}
                      </p>
                      {ev.failure_reason && <p className="text-xs text-red-500 mt-1">{ev.failure_reason}</p>}
                    </div>
                    <Button size="sm" variant="ghost" className="shrink-0" onClick={() => setExpandedEvent(expandedEvent === ev.event_id ? null : ev.event_id)}>
                      {expandedEvent === ev.event_id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                  </div>
                  {expandedEvent === ev.event_id && (
                    <div className="mt-3 pt-3 border-t border-stone-100 space-y-2">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><span className="text-stone-400">Event ID:</span> <span className="text-stone-600 font-mono">{ev.event_id}</span></div>
                        <div><span className="text-stone-400">Channel:</span> <span className="text-stone-600">{ev.channel}</span></div>
                        <div><span className="text-stone-400">Sent at:</span> <span className="text-stone-600">{ev.sent_at || "N/A"}</span></div>
                        <div><span className="text-stone-400">Delivered at:</span> <span className="text-stone-600">{ev.delivered_at || "N/A"}</span></div>
                        <div><span className="text-stone-400">Read at:</span> <span className="text-stone-600">{ev.read_at || "N/A"}</span></div>
                        <div><span className="text-stone-400">Retries:</span> <span className="text-stone-600">{ev.retry_count || 0}</span></div>
                        {ev.meta_message_id && <div className="col-span-2"><span className="text-stone-400">Meta ID:</span> <span className="text-stone-600 font-mono">{ev.meta_message_id}</span></div>}
                      </div>
                      {ev.status_history?.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-stone-500 mb-1">Status history</p>
                          <div className="space-y-1">
                            {ev.status_history.map((sh, i) => (
                              <div key={i} className="flex items-center gap-2 text-xs">
                                <div className="w-1.5 h-1.5 rounded-full bg-stone-300" />
                                <span className="text-stone-600">{sh.status}</span>
                                <span className="text-stone-400">{sh.timestamp?.split("T")[0]} {sh.timestamp?.split("T")[1]?.split(".")[0]}</span>
                                {sh.reason && <span className="text-red-500">{sh.reason}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          ) : (
            <Card className="rounded-xl border-stone-200/60 p-12 text-center">
              <Mail className="w-10 h-10 text-stone-300 mx-auto mb-3" />
              <p className="text-sm text-stone-500">No messages sent yet.</p>
            </Card>
          )}
        </div>
      )}

      {/* TAB: Templates */}
      {tab === "templates" && (
        <div className="space-y-3">
          {Object.entries(templates).map(([id, tpl]) => (
            <Card key={id} className="rounded-xl border-stone-200/60 p-4">
              {editingTemplate === id ? (
                <div className="space-y-3">
                  <h4 className="text-sm font-bold text-stone-900">{tpl.name}</h4>
                  <Input placeholder="Email subject" value={templateForm.subject} onChange={e => setTemplateForm({ ...templateForm, subject: e.target.value })} className="rounded-lg text-sm" />
                  <Textarea placeholder="Email body" value={templateForm.email_body} onChange={e => setTemplateForm({ ...templateForm, email_body: e.target.value })} rows={4} className="rounded-lg text-sm" />
                  <Textarea placeholder="WhatsApp message" value={templateForm.whatsapp_body} onChange={e => setTemplateForm({ ...templateForm, whatsapp_body: e.target.value })} rows={2} className="rounded-lg text-sm" />
                  <Input placeholder="WA template name (approved in Meta)" value={templateForm.wa_template_name} onChange={e => setTemplateForm({ ...templateForm, wa_template_name: e.target.value })} className="rounded-lg text-sm" />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleSaveTemplate} disabled={savingTemplate} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg">
                      {savingTemplate ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Save
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingTemplate(null)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-bold text-stone-900">{tpl.name}</h4>
                      <Badge variant="outline" className="text-xs capitalize">{tpl.category}</Badge>
                      {tpl.wa_template_name && <Badge className="bg-green-50 text-green-700 border-green-200 text-xs">WA: {tpl.wa_template_name}</Badge>}
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="text-xs" onClick={() => { setEditingTemplate(id); setTemplateForm({ subject: tpl.subject || "", email_body: tpl.email_body || "", whatsapp_body: tpl.whatsapp_body || "", wa_template_name: tpl.wa_template_name || "" }); }} data-testid={`edit-template-${id}`}>
                        Edit
                      </Button>
                      {tpl.updated_by && (
                        <Button size="sm" variant="ghost" className="text-xs text-stone-400" onClick={() => handleResetTemplate(id)}>Reset</Button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-stone-500 mb-1"><span className="font-medium">Subject:</span> {tpl.subject}</p>
                  <p className="text-xs text-stone-400 line-clamp-2">{tpl.email_body?.substring(0, 150)}...</p>
                  <p className="text-xs text-green-600 mt-1 line-clamp-1">WA: {tpl.whatsapp_body?.substring(0, 100)}...</p>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* TAB: Failures */}
      {tab === "failures" && (
        <div className="space-y-3">
          {failures.length > 0 ? (
            failures.map(f => (
              <Card key={f.event_id} className="rounded-xl border-red-200/50 bg-red-50/20 p-4">
                <div className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-stone-900">{f.patient_name || f.patient_id}</span>
                      <Badge variant="outline" className="text-xs">{f.channel}</Badge>
                      <Badge variant="outline" className="text-xs">{(f.template_id || "").replace(/_/g, " ")}</Badge>
                    </div>
                    <p className="text-xs text-red-600 mt-1">{f.failure_reason || "Unknown error"}</p>
                    <p className="text-xs text-stone-400 mt-0.5">Retries: {f.retry_count || 0}/3 - {f.created_at?.split("T")[0]}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {(f.retry_count || 0) < 3 && (
                      <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={() => handleRetry(f.event_id)} data-testid={`retry-${f.event_id}`}>
                        <RotateCcw className="w-3 h-3 mr-1" /> Retry
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-xs" onClick={() => handleResolveComm(f.event_id)}>Resolve</Button>
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <Card className="rounded-xl border-stone-200/60 p-12 text-center">
              <CheckCircle2 className="w-10 h-10 text-green-300 mx-auto mb-3" />
              <p className="text-sm text-stone-500">No failed communications. All clear.</p>
            </Card>
          )}
        </div>
      )}

      {/* TAB: Exceptions (Failure Workflows) */}
      {tab === "exceptions" && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <Button onClick={handleDetectFailures} variant="outline" className="rounded-lg text-sm" data-testid="detect-failures-btn">
              <Search className="w-4 h-4 mr-1" /> Scan for Issues
            </Button>
          </div>

          {exceptions.length > 0 ? (
            exceptions.map(ex => (
              <Card key={ex.exception_id} className={`rounded-xl p-4 ${ex.status === "resolved" ? "border-stone-200/60 opacity-60" : ex.severity === "high" || ex.severity === "critical" ? "border-red-200 bg-red-50/20" : "border-amber-200 bg-amber-50/20"}`}>
                <div className="flex items-start gap-3">
                  <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${ex.severity === "high" || ex.severity === "critical" ? "text-red-500" : "text-amber-500"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-stone-900">{(ex.workflow_type || "").replace(/_/g, " ")}</span>
                      {severityBadge(ex.severity)}
                      <Badge variant="outline" className={`text-xs ${ex.status === "resolved" ? "text-green-600" : "text-stone-500"}`}>
                        {ex.status}
                      </Badge>
                    </div>
                    {ex.patient_name && <p className="text-xs text-stone-600 mt-0.5">Patient: {ex.patient_name} ({ex.patient_email})</p>}
                    <p className="text-xs text-stone-500 mt-0.5">{ex.details}</p>
                    <p className="text-xs text-stone-400 mt-0.5">{ex.created_at?.split("T")[0]}</p>
                    {ex.resolution_notes && <p className="text-xs text-green-600 mt-1">Resolution: {ex.resolution_notes}</p>}
                  </div>
                  {ex.status !== "resolved" && (
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={() => handleEscalate(ex.exception_id)}>
                        <ArrowUpRight className="w-3 h-3 mr-1" /> Escalate
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={() => setResolvingId(resolvingId === ex.exception_id ? null : ex.exception_id)}>
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Resolve
                      </Button>
                    </div>
                  )}
                </div>
                {resolvingId === ex.exception_id && (
                  <div className="mt-3 pt-3 border-t border-stone-100 flex gap-2">
                    <Input placeholder="Resolution notes..." value={resolveNotes} onChange={e => setResolveNotes(e.target.value)} className="rounded-lg text-sm flex-1" />
                    <Button size="sm" onClick={() => handleResolveException(ex.exception_id)} className="bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs">Confirm</Button>
                  </div>
                )}
              </Card>
            ))
          ) : (
            <Card className="rounded-xl border-stone-200/60 p-12 text-center">
              <CheckCircle2 className="w-10 h-10 text-green-300 mx-auto mb-3" />
              <p className="text-sm text-stone-500">No open exceptions. Run a scan to detect issues.</p>
            </Card>
          )}
        </div>
      )}

      {/* TAB: Automation */}
      {tab === "automation" && (
        <div className="space-y-4">
          <Card className="rounded-xl border-stone-200/60 p-5">
            <h3 className="text-sm font-bold text-stone-900 mb-2">Automated appointment reminders</h3>
            <p className="text-xs text-stone-500 mb-3">
              The system checks for upcoming appointments every 10 minutes and sends reminders automatically:
            </p>
            <ul className="text-xs text-stone-600 space-y-1.5 mb-4">
              <li className="flex items-center gap-2"><Bell className="w-3.5 h-3.5 text-[#FF7A00]" /> <strong>48 hours before:</strong> Email {waStatus?.configured && "+ WhatsApp"} reminder with appointment details and a reschedule link</li>
              <li className="flex items-center gap-2"><Bell className="w-3.5 h-3.5 text-[#FF7A00]" /> <strong>30 minutes before:</strong> Email {waStatus?.configured && "+ WhatsApp"} reminder with meeting link</li>
            </ul>
            <Button variant="outline" className="rounded-lg text-sm" onClick={handleTriggerReminders} data-testid="trigger-reminders-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Run Reminder Check Now
            </Button>
          </Card>

          <Card className="rounded-xl border-stone-200/60 p-5">
            <h3 className="text-sm font-bold text-stone-900 mb-2">Failure detection workflows</h3>
            <p className="text-xs text-stone-500 mb-3">
              The system monitors for common failure conditions that need staff attention:
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              {Object.entries({
                payment_without_booking: { icon: "CreditCard", desc: "Paid but not booked" },
                incomplete_intake: { icon: "FileText", desc: "Booked but intake missing" },
                whatsapp_failure: { icon: "MessageSquare", desc: "WA message failed" },
                email_bounce: { icon: "Mail", desc: "Email bounced" },
                no_show: { icon: "XCircle", desc: "Missed appointment" },
                meet_failure: { icon: "Video", desc: "Meeting link issue" },
                report_delivery_failure: { icon: "FileText", desc: "Report not delivered" },
              }).map(([key, val]) => (
                <div key={key} className="flex items-center gap-2 text-xs text-stone-600 bg-stone-50 rounded-lg p-2.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                  <span className="font-medium">{key.replace(/_/g, " ")}</span>
                  <span className="text-stone-400">- {val.desc}</span>
                </div>
              ))}
            </div>
            <Button variant="outline" className="rounded-lg text-sm" onClick={handleDetectFailures} data-testid="scan-failures-btn">
              <Search className="w-4 h-4 mr-1" /> Run Full Scan
            </Button>
          </Card>

          <Card className="rounded-xl border-stone-200/60 p-5">
            <h3 className="text-sm font-bold text-stone-900 mb-2">WhatsApp Business Platform</h3>
            {waStatus?.configured ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2"><Wifi className="w-4 h-4 text-green-600" /><span className="text-sm text-green-700 font-medium">Connected</span></div>
                <div className="grid grid-cols-2 gap-2 text-xs text-stone-600">
                  <div>Graph API: {waStatus.graph_version}</div>
                  <div>Phone Number ID: Set</div>
                  <div>Access Token: Set</div>
                  <div>App Secret: {waStatus.app_secret_set ? "Set" : "Not set"}</div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2"><WifiOff className="w-4 h-4 text-amber-600" /><span className="text-sm text-amber-700 font-medium">Not configured</span></div>
                <p className="text-xs text-stone-500">To enable WhatsApp Business messaging, add the following to your environment:</p>
                <ul className="text-xs text-stone-600 space-y-1 font-mono bg-stone-50 rounded-lg p-3">
                  <li>META_PHONE_NUMBER_ID=your_phone_number_id</li>
                  <li>META_ACCESS_TOKEN=your_system_user_token</li>
                  <li>META_APP_SECRET=your_app_secret</li>
                </ul>
                <p className="text-xs text-stone-400">Get these from <a href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer" className="text-[#FF7A00] underline">Meta for Developers</a></p>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color = "text-stone-700", onClick, active }) => (
  <Card className={`rounded-xl border-stone-200/60 p-3 text-center cursor-pointer hover:shadow-md hover:border-[#FF7A00]/30 transition-all ${active ? "ring-2 ring-[#FF7A00]/40 border-[#FF7A00]/30 bg-[#FFF8F2]" : ""}`} onClick={onClick}>
    <Icon className={`w-4 h-4 mx-auto mb-1 ${color}`} />
    <p className={`text-lg font-bold ${color}`}>{value || 0}</p>
    <p className="text-xs text-stone-400">{label}</p>
  </Card>
);

export default CommunicationsPage;
